import { GeneralConfig } from '@/core/configs/general-config'
import { AuthService } from '@/core/auth/services/AuthService.ts'
import * as op from '../errors/OperationResult'
import { type OperationResult } from '../errors/OperationResult'
import { delaySeconds } from '../async-utils'

export class HttpClientConfig {
  public readonly simulateLatencySeconds?: number =
    // 0.3
    0
}

export class HttpClientService {
  constructor(
    private readonly generalConfig: GeneralConfig,
    private readonly config: HttpClientConfig,
    private readonly authService: AuthService,
  ) {}

  public async sendRequest(
    endpoint: string,
    method: string,
    options: {
      headers?: Record<string, string>
      queryParams?: Record<string, string | number | boolean>
      body?: object
      logError?: boolean
      abortSignal?: AbortSignal
      skipAuth?: boolean
    } = {},
  ): Promise<OperationResult<object | undefined>> {
    const { logError = true, skipAuth = false } = options

    const url = new URL(`${this.generalConfig.ServerHost}${endpoint}`)

    if (options.queryParams) {
      for (const [key, value] of Object.entries(options.queryParams)) {
        url.searchParams.set(key, String(value))
      }
    }

    const authHeaders: Record<string, string> = {}
    if (!skipAuth) {
      const authToken = this.authService.getAuthToken()
      if (!authToken) {
        return op.error('No Authorization Token')
      }
      authHeaders['Authorization'] = `Bearer ${authToken}`
    }

    const fetchOptions: RequestInit = {
      method,
      headers: {
        ...options.headers,
        ...authHeaders,
      },
      signal: options.abortSignal,
    }

    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body)
    }

    let response: Response
    try {
      response = await fetch(url.toString(), fetchOptions)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      logErrorConditional(`error sending http request: ${message}`)
      return op.error(message)
    } finally {
      if (this.config.simulateLatencySeconds) {
        await delaySeconds(this.config.simulateLatencySeconds)
      }
    }

    if (!response.ok) {
      const errorMessage = await this.getErrorMessage(response)
      logErrorConditional(`error in http response: ${errorMessage}`)
      return op.error(errorMessage)
    }

    const text = await response.text()
    const responseBody = text ? (JSON.parse(text) as object) : null
    return op.success(responseBody)

    function logErrorConditional(errorMessage: string) {
      if (logError) {
        console.error(errorMessage)
      }
    }
  }

  private async getErrorMessage(response: Response): Promise<string> {
    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
      const errorBody = (await response.json()) as unknown
      return JSON.stringify(errorBody)
    }

    const text = await response.text()
    return text || `HTTP ${response.status}`
  }
}
